import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import { useFilters } from "../lib/FilterContext";
import { useLocations } from "../components/LocationPicker";
import { DIETARY_TAGS } from "../lib/format";

const Section = ({ title, children }) => (
  <div className="tup-card p-5">
    <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#8A93A6] mb-3">
      {title}
    </div>
    {children}
  </div>
);

const Pill = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
      active
        ? "bg-[#277A82] text-white border-[#277A82]"
        : "bg-white text-[#4F5D75] border-[#E2E0D8] hover:border-[#277A82]"
    }`}
  >
    {children}
  </button>
);

const Filters = () => {
  const { filters, setFilters, reset } = useFilters();
  const { locations } = useLocations();
  const update = (k, v) => setFilters({ ...filters, [k]: v });

  // Heal a stale `location_id` if the user deleted that storage location
  // while it was selected. Without this, the Filters UI would have no pill
  // highlighted, the filter would still apply, and the user couldn't reset
  // it without a full "Reset to defaults".
  useEffect(() => {
    if (
      filters.location_id &&
      filters.location_id !== "all" &&
      locations.length > 0 &&
      !locations.some((l) => l.id === filters.location_id)
    ) {
      update("location_id", "all");
    }
  }, [locations, filters.location_id]);

  return (
    <div className="px-5 pt-6 pb-4">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#8A93A6]">Filters</div>
        <h1 className="font-heading text-3xl font-bold text-[#2D3142] mt-1">Tune your view</h1>
        <p className="text-[#4F5D75] mt-1 text-sm">
          Filters apply to Home and Inventory.
        </p>
      </motion.div>

      <div className="mt-6 space-y-4">
        <Section title="Status">
          <div className="flex flex-wrap gap-2">
            {[
              ["fresh", "Active"],
              ["all", "All"],
              ["consumed", "Consumed"],
              ["discarded", "Discarded"],
            ].map(([v, label]) => (
              <Pill
                key={v}
                active={filters.status === v}
                onClick={() => update("status", v)}
                testid={`filter-status-${v}`}
              >
                {label}
              </Pill>
            ))}
          </div>
        </Section>

        <Section title="Storage location">
          <div className="flex flex-wrap gap-2">
            <Pill
              active={filters.location_id === "all"}
              onClick={() => update("location_id", "all")}
              testid="filter-location-all"
            >
              All
            </Pill>
            {locations.map((loc) => (
              <Pill
                key={loc.id}
                active={filters.location_id === loc.id}
                onClick={() => update("location_id", loc.id)}
                testid={`filter-location-${loc.id}`}
              >
                {loc.name}
              </Pill>
            ))}
          </div>
        </Section>

        <Section title="Dietary tag">
          <div className="flex flex-wrap gap-2">
            <Pill
              active={filters.dietary_tag === "all"}
              onClick={() => update("dietary_tag", "all")}
              testid="filter-dietary-all"
            >
              All
            </Pill>
            {DIETARY_TAGS.map((t) => (
              <Pill
                key={t}
                active={filters.dietary_tag === t}
                onClick={() => update("dietary_tag", t)}
                testid={`filter-dietary-${t}`}
              >
                {t}
              </Pill>
            ))}
          </div>
        </Section>

        <Section title="Calories per serving">
          <div className="flex flex-wrap gap-2">
            {[
              ["all", "All"],
              ["under-300", "Under 300"],
              ["300-500", "300–500"],
              ["500-plus", "500+"],
            ].map(([v, label]) => (
              <Pill
                key={v}
                active={filters.calorie_range === v}
                onClick={() => update("calorie_range", v)}
                testid={`filter-cal-${v}`}
              >
                {label}
              </Pill>
            ))}
          </div>
        </Section>

        <Section title="Sort">
          <div className="flex flex-wrap gap-2">
            {[
              ["expiring_soon", "Expiring soonest"],
              ["newest", "Newest"],
              ["calories_asc", "Calories ↑"],
              ["calories_desc", "Calories ↓"],
              ["color", "By color"],
            ].map(([v, label]) => (
              <Pill
                key={v}
                active={filters.sort === v}
                onClick={() => update("sort", v)}
                testid={`filter-sort-${v}`}
              >
                {label}
              </Pill>
            ))}
          </div>
          <p className="text-[11px] text-[#8A93A6] mt-3 leading-snug">
            "By color" groups every matching color together. Boxes with no color set
            appear at the top; spoilage-priority breaks ties inside each color band.
          </p>
        </Section>

        <Button
          onClick={reset}
          data-testid="filter-reset-btn"
          variant="outline"
          className="w-full border-[#E2E0D8] text-[#4F5D75]"
        >
          <RotateCcw className="w-4 h-4 mr-2" /> Reset to defaults
        </Button>
      </div>
    </div>
  );
};

export default Filters;
